/**
 * iter87 — Dealer-timezone-aware date boundary helpers.
 *
 * All timestamps in the API are UTC. UI metrics like "Leads This Month"
 * should be computed in the DEALER's local timezone, not the viewer's
 * browser timezone. The backend supplies the dealer's IANA TZ on the
 * /auth/me payload (`business_timezone`). These helpers take that TZ
 * and return UTC millisecond boundaries the caller can compare against
 * `new Date(lead.created_at).getTime()`.
 */

const DEFAULT_TZ = "America/New_York";

/**
 * Get the {year, month, day} reading of `dateMs` (UTC millis) in the
 * given IANA timezone. Uses Intl.DateTimeFormat which respects DST
 * automatically. month is 1-indexed (1-12).
 */
function dateParts(dateMs, timezone) {
  try {
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const parts = fmt.formatToParts(new Date(dateMs));
    const get = (t) => parts.find((p) => p.type === t)?.value;
    return {
      year: Number(get("year")),
      month: Number(get("month")), // 1-12
      day: Number(get("day")),
      hour: Number(get("hour")) % 24,
      minute: Number(get("minute")),
      second: Number(get("second")),
    };
  } catch {
    // Fallback if the TZ string is invalid: use browser local.
    const d = new Date(dateMs);
    return {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate(),
      hour: d.getHours(),
      minute: d.getMinutes(),
      second: d.getSeconds(),
    };
  }
}

/**
 * Given a TZ and a target wall-clock instant in that TZ, compute the
 * matching UTC millisecond. Uses a two-pass correction to handle DST
 * boundaries correctly without bringing in a TZ library.
 */
function wallToUtcMs(timezone, year, month, day, hour = 0, minute = 0, second = 0) {
  // Treat the target as if it were UTC, then read it back in `timezone`
  // and adjust by the delta. One pass handles standard offsets; a second
  // pass corrects DST cusp cases.
  let utc = Date.UTC(year, month - 1, day, hour, minute, second);
  for (let i = 0; i < 2; i++) {
    const p = dateParts(utc, timezone);
    const seenUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    const targetUtc = Date.UTC(year, month - 1, day, hour, minute, second);
    const delta = targetUtc - seenUtc;
    if (delta === 0) break;
    utc += delta;
  }
  return utc;
}

/**
 * Returns the UTC millisecond for "00:00:00 on the 1st of the current
 * calendar month" in the given IANA timezone.
 *
 * Example: at 2026-06-01T03:30:00Z (June 1 in UTC), with
 * timezone="America/Los_Angeles" (UTC-7), the local wall clock is
 * 2026-05-31T20:30 PDT — so the start of the *current* calendar month
 * in LA is 2026-05-01T00:00 PDT = 2026-05-01T07:00Z.
 */
export function startOfCurrentMonthMs(timezone = DEFAULT_TZ, now = Date.now()) {
  const p = dateParts(now, timezone);
  return wallToUtcMs(timezone, p.year, p.month, 1, 0, 0, 0);
}

/**
 * Returns the UTC millisecond for "00:00:00 today" in the given TZ.
 * Useful for "Today's …" metrics.
 */
export function startOfTodayMs(timezone = DEFAULT_TZ, now = Date.now()) {
  const p = dateParts(now, timezone);
  return wallToUtcMs(timezone, p.year, p.month, p.day, 0, 0, 0);
}

/**
 * Returns the UTC millisecond N days ago at 00:00 in the given TZ.
 * Used for the "Total Leads" / "Verified Leads" rolling window so the
 * TimeRangeSelector aligns with the dealer's calendar boundary.
 */
export function startNDaysAgoMs(daysAgo, timezone = DEFAULT_TZ, now = Date.now()) {
  const today = startOfTodayMs(timezone, now);
  return today - daysAgo * 24 * 60 * 60 * 1000;
}
